import List from "../../../../util/datastructures/list/List";
import EventDispatcher from "../../../../util/EventDispatcher";
import DialogEvent from "./DialogEvent";

export class Dialog extends EventDispatcher {
    constructor(maxTime, dialogElementFactory) {
        super();

        this.maxTime = (maxTime != null ? maxTime : 0);
        this.dialogElementFactory = (dialogElementFactory != null ? dialogElementFactory : new DialogElementFactory());
        this.states = [];
        this.transitions = [];
        this._maxTime = maxTime;
        this._startState = null;
        this._timeOutState = null;
        this._data = null;
    }

    destroy() {
        for (var i = 0; i < this.states.length; i++) {
            this.states[i].destroy();
            this.states = null;
        }

        for (var i = 0; i < this.transitions.length; i++) {
            this.transitions[i].destroy();
            this.transitions = null;
        }
        this._startState = null;
        this._timeOutState = null;
        this.dialogElementFactory = null;
    }

    getData() {
        return this._data;
    }

    setData(value) {
        this._data = value;
        this.dispatchEvent(DialogEvent.DATA_CHANGED, value);
    }

    getStartState() {
        return this._startState;
    }

    setStartState(value) {
        this._startState = value;
        this.dispatchEvent(DialogEvent.START_STATE_CHANGED, value);
    }

    getMaxTime() {
        return this._maxTime;
    }

    setMaxTime(value) {
        this._maxTime = value;
        this.dispatchEvent(DialogEvent.TIME_OUT_STATE_CHANGED, value);
    }

    getTimeOutState() {
        return this._timeOutState;
    }

    setTimeOutState(value) {
        this._timeOutState = value;
        this.dispatchEvent(DialogEvent.TIME_OUT_STATE_CHANGED, value);
    }

    addState() {
        const state = this.dialogElementFactory.getDialogState();
        this._addState(state);
        return state;
    }

    removeState(state) {
        const index = this.states.indexOf(state);
        if (index != -1) {
            this.states.splice(index, 1);
        }

        this.dispatchEvent(DialogEvent.STATE_REMOVED, state);
    }

    addTransition(state) {
        const transition = this.dialogElementFactory.getDialogTransition(state);
        this._addTransition(transition);
        return transition;
    }

    removeTransition(transition) {
        const index = this.transitions.indexOf(transition);
        if (index != -1) {
            this.transitions.splice(index, 1);
        }

        this.dispatchEvent(DialogEvent.TRANSITION_REMOVED, transition);
    }

    // ---------- Protected

    _addState(state) {
        this.states.push(state);
        this.dispatchEvent(DialogEvent.STATE_ADDED, state);
    }

    _addTransition(transition) {
        this.transitions.push(transition);
        this.dispatchEvent(DialogEvent.TRANSITION_ADDED, transition);
    }
}

// ==================================================================================

export class DialogCommand extends EventDispatcher {
    constructor(data, condition, executeOnce) {
        super();

        this._data = data;
        this._condition = condition;
        this._executeOnce = executeOnce;
        this._metaData = null;
    }

    destroy() {
        this._data = null;
        this._condition = null;
    }

    getData() {
        return this._data;
    }

    setData(value) {
        this._data = value;
        this.dispatchEvent(DialogEvent.DATA_CHANGED, value);
    }

    isExecuteOnce() {
        return this._executeOnce;
    }

    setExecuteOnce(value) {
        this._executeOnce = value;
        this.dispatchEvent(DialogEvent.EXECUTE_ONCE_CHANGED, value);
    }

    getCondition() {
        return this._condition;
    }

    setCondition(value) {
        this._condition = value;
        this.dispatchEvent(DialogEvent.CONDITION_CHANGED, value);
    }

    getMetaData() {
        return this._metaData;
    }

    setMetaData(value) {
        this._metaData = value;
        this.dispatchEvent(DialogEvent.META_DATA_CHANGED, value);
    }
}

// ==================================================================================

export class DialogCommandSequence extends EventDispatcher {
    constructor(dialogElementFactory) {
        super();

        this.dialogElementFactory = dialogElementFactory;

        this._commands = new List();
        this._metaData = null;
        this.dialogElementFactory = dialogElementFactory;
    }

    destroy() {
        const commandsIterator = this._commands.getIterator();
        while (commandsIterator.hasNext()) {
            const command = commandsIterator.next();
            command.destroy();
        }

        this._commands = null;
        this._metaData = null;
    }

    getCommandsIterator() {
        return this._commands.getIterator();
    }

    getMetaData() {
        return this._metaData;
    }

    setMetaData(value) {
        this._metaData = value;
        this.dispatchEvent(DialogEvent.META_DATA_CHANGED, value);
    }

    addCommand(data, condition, executeOnce) {
        const command = this.dialogElementFactory.getDialogCommand(data, condition, executeOnce);
        this._addCommand(command);
        return command;
    }

    addCommandAt(index, data, condition, executeOnce) {
        const newCommand = this.dialogElementFactory.getDialogCommand(data, condition, executeOnce);
        this._addCommandAt(newCommand, index);
        return newCommand;
    }

    addCommandBefore(command, data, condition, executeOnce) {
        const newCommand = this.dialogElementFactory.getDialogCommand(data, condition, executeOnce);
        this._addCommandBefore(newCommand, command);
    }

    addCommandAfter(command, data, condition, executeOnce) {
        const newCommand = this.dialogElementFactory.getDialogCommand(data, condition, executeOnce);
        this._addCommandAfter(newCommand, command);
    }

    removeCommand(command) {
        this._commands.remove(command);
    }

    getCommandIndex(command) {
        let result = -1;
        let index = 0;
        const iterator = this._commands.getIterator();
        while (iterator.hasNext()) {
            const currentCommand = iterator.next();
            if (currentCommand == command) {
                result = index;
                break;
            }
            index++;
        }
        return result;
    }

    getCommandAt(index) {
        if (this._commands.size() > index) {
            const iterator = this._commands.getIterator();
            let currentIndex = 0;
            while (currentIndex <= index) {
                var command = iterator.next();
                currentIndex++;
            }
        }
        else {
            throw "Supplied index is out of bounds!";
        }
        return command;
    }

    // -------------- Private ------------------

    _addCommand(command) {
        this._commands.push(command);
        this.dispatchEvent(DialogEvent.COMMAND_ADDED, command);
    }

    _addCommandBefore(commandToAdd, targetCommand) {
        this._commands.insertBefore(commandToAdd, targetCommand);
        this.dispatchEvent(DialogEvent.COMMAND_ADDED, commandToAdd);
    }

    _addCommandAt(command, index) {
        const size = this._commands.size;
        if (size > index) {
            let currentIndex = 0;
            const iterator = this._commands.getIterator();
            while (currentIndex <= index) {
                var targetCommand = iterator.next();
                currentIndex++;
            }
            this._commands.insertBefore(command, targetCommand);
        }
        else if (size < index) {
            throw "Specified index is out of range!!";
        }
        else {
            this._commands.push(command);
        }

        this.dispatchEvent(DialogEvent.COMMAND_ADDED, command);
    }

    _addCommandAfter(commandToAdd, targetCommand) {
        this._commands.insertAfter(commandToAdd, targetCommand);
        this.dispatchEvent(DialogEvent.COMMAND_ADDED, commandToAdd);
    }
}

// ==================================================================================

export class DialogElementFactory {
    constructor() {
    }
    getDialogState() {
        return new DialogState(this);
    }
    getDialogTransition(state) {
        return new DialogTransition(this, state);
    }
    getDialogCommand(data, condition, executeOnce) { 
        return new DialogCommand(data, condition, executeOnce); 
    }
    getDialogOption(commandSequence, data, condition, autoTake, executeOnce) { 
        return new DialogOption(commandSequence, data, condition, autoTake, executeOnce); 
    }
}





// ==================================================================================

export class DialogOption extends DialogCommand {
    constructor(commandSequence, data, condition, autoTake, executeOnce) {
        super(data, condition, executeOnce);
        this._id = -1;
        this._commandSequence = commandSequence;
        this._autoTake = autoTake;
    }

    destroy() {
        this._commandSequence = null;
        super.destroy();
    }

    getCommandSequence() {
        return this._commandSequence;
    }

    setCommandSequence(value) {
        this._commandSequence = value;
        this.dispatchEvent(DialogEvent.OPTION_TARGET_CHANGED, value);
    }

    getAutoTake() {
        return this._autoTake;
    }

    setAutoTake(value) {
        this._autoTake = value;
        this.dispatchEvent(DialogEvent.AUTO_TAKE_CHANGED, value);
    }
}

// ==================================================================================

export class DialogState extends DialogCommandSequence {
    constructor(dialogElementFactory) {
        super(dialogElementFactory);
    }

    addOption(commandSequence, data, condition, autoTake, executeOnce) {
        const option = this.dialogElementFactory.getDialogOption(commandSequence, data, condition, autoTake, executeOnce);
        this._addOption(option);
        return option;
    }

    addOptionBefore(command, commandSequence, data, condition, autoTake, executeOnce) {
        const option = this.dialogElementFactory.getDialogOption(commandSequence, data, condition, autoTake, executeOnce);
        this._addOptionBefore(option, command);
        return option;
    }

    addOptionAt(index, commandSequence, data, condition, autoTake, executeOnce) {
        const option = this.dialogElementFactory.getDialogOption(commandSequence, data, condition, autoTake, executeOnce);
        this._addOptioAt(option, index);
        return option;
    }

    addOptionAfter(command, commandSequence, data, condition, autoTake, executeOnce) {
        const option = this.dialogElementFactory.getDialogOption(commandSequence, data, condition, autoTake, executeOnce);
        this._addOptionAfter(option, command);
        return option;
    }

    removeOption(option) {
        this._commands.remove(option);
        this.dispatchEvent(DialogEvent.OPTION_REMOVED, option);
    }

    // -------------- Protected -----------------------

    _addOption(option) {
        this._commands.push(option);
        this.dispatchEvent(DialogEvent.OPTION_ADDED, option);
    }

    _addOptionBefore(optionToAdd, targetCommand) {
        this._commands.insertBefore(optionToAdd, targetCommand);
        this.dispatchEvent(DialogEvent.OPTION_ADDED, optionToAdd);
    }

    _addOptionAt(option, index) {
        const size = this._commands.size;
        if (size > index) {
            let currentIndex = 0;
            const iterator = this._commands.getIterator();
            while (currentIndex <= index) {
                var targetCommand = iterator.next();
                currentIndex++;
            }
            this._commands.insertBefore(option, targetCommand);
        }
        else if (size < index) {
            throw "Specified index is out of range!!";
        }
        else {
            this._commands.push(option);
        }

        this.dispatchEvent(DialogEvent.OPTION_ADDED, option);
    }

    _addOptionAfter(optionToAdd, targetCommand) {
        this._commands.insertAfter(optionToAdd, targetCommand);
        this.dispatchEvent(DialogEvent.OPTION_ADDED, optionToAdd);
    }
}

// ==================================================================================

export class DialogTransition extends DialogCommandSequence {
    constructor(dialogElementFactory, state) {
        super(dialogElementFactory);

        this._state = state;
    }

    destroy() {
        this._state = null;
        super.destroy();
    }

    getState() {
        return this._state;
    }

    setState(value) {
        this._state = value;
        this.dispatchEvent(DialogEvent.TRANSITION_TARGET_CHANGED, value);
    }
}